/** View-model derivation for the Process Detail screen — run grouping, KPI
 *  derivation and the pk → ProcessDetail resolver (templated rich body). */
import type { ExecKpi, ProcessDetail, RunRow, RunStatus } from '@wand/shared/dataset/process_detail.js';
import type { ProcessRow } from '@wand/shared';
import { procMeta } from './processes.js';

/** Which KPI/filter bucket a run status counts toward (exceptions count as failures). */
export const runGroup = (s: RunStatus): 'running' | 'awaiting' | 'risk' | 'failed' | 'completed' =>
  s === 'exception' ? 'failed' : s;

/** Execution KPIs are derived live from the runs, so they always reconcile with
 *  the list below them (matching the design, which computes them the same way). */
export function computeExecKpis(runs: RunRow[], avgDuration: string): ExecKpi[] {
  const n = (g: ReturnType<typeof runGroup>) => runs.filter((r) => runGroup(r.status) === g).length;
  const running = n('running'),
    awaiting = n('awaiting'),
    risk = n('risk'),
    failed = n('failed');
  const attention = risk + awaiting + failed;
  return [
    {
      label: 'Running now',
      value: String(running),
      sub: 'in progress',
      valueColor: running > 0 ? 'var(--ink)' : 'var(--disabled)',
    },
    {
      label: 'Needs attention',
      value: String(attention),
      sub: attention > 0 ? 'act soon' : 'all clear',
      valueColor: attention > 0 ? 'var(--amber-tx)' : 'var(--green)',
    },
    {
      label: 'At risk (SLA)',
      value: String(risk),
      sub: 'over target',
      valueColor: risk > 0 ? 'var(--red)' : 'var(--ink)',
    },
    {
      label: 'Failed / exceptions',
      value: String(failed),
      sub: 'need a fix',
      valueColor: failed > 0 ? 'var(--red)' : 'var(--ink)',
    },
    { label: 'Avg duration', value: avgDuration, sub: 'per run', valueColor: 'var(--ink)' },
  ];
}

/** "8 runs · 1 running · 2 failed" — derived from the runs, matching the design. */
export function runSummary(runs: RunRow[]): string {
  const n = (g: ReturnType<typeof runGroup>) => runs.filter((r) => runGroup(r.status) === g).length;
  return `${runs.length} runs · ${n('running')} running · ${n('failed')} failed`;
}

function template(
  agent: string,
): Omit<
  ProcessDetail,
  'pk' | 'name' | 'cat' | 'agent' | 'version' | 'status' | 'icon' | 'color' | 'bg'
> {
  const runs: RunRow[] = [
    {
      id: 'r1',
      name: 'Alex Rivera',
      ref: 'ONB-2029',
      tintIdx: 0,
      status: 'failed',
      stageLabel: 'Stopped · Day 1',
      stageDone: 2,
      stageTotal: 4,
      started: 'Jul 6',
      duration: '2d',
      sla: 'Halted',
    },
    {
      id: 'r2',
      name: 'Nina Park',
      ref: 'ONB-2019',
      tintIdx: 3,
      status: 'exception',
      stageLabel: 'Stopped · First Week',
      stageDone: 3,
      stageTotal: 4,
      started: 'Jun 30',
      duration: '8d',
      sla: 'Halted',
    },
    {
      id: 'r3',
      name: 'Maya Chen',
      ref: 'ONB-2041',
      tintIdx: 1,
      status: 'running',
      stageLabel: 'Day 1',
      stageDone: 2,
      stageTotal: 4,
      started: 'Jul 7',
      duration: '3d',
      sla: 'On track',
    },
    {
      id: 'r4',
      name: 'Jordan Lee',
      ref: 'ONB-2038',
      tintIdx: 2,
      status: 'risk',
      stageLabel: 'First Week',
      stageDone: 2,
      stageTotal: 4,
      started: 'Jun 28',
      duration: '9d',
      sla: 'At risk',
    },
    {
      id: 'r5',
      name: 'Tom Becker',
      ref: 'ONB-2044',
      tintIdx: 4,
      status: 'awaiting',
      stageLabel: 'Pre-boarding',
      stageDone: 1,
      stageTotal: 4,
      started: 'Jul 9',
      duration: '4h',
      sla: 'Paused',
    },
    {
      id: 'r6',
      name: 'Priya Anand',
      ref: 'ONB-2055',
      tintIdx: 5,
      status: 'completed',
      stageLabel: 'Completed',
      stageDone: 4,
      stageTotal: 4,
      started: 'May 30',
      duration: '11d',
      sla: 'Met',
    },
    {
      id: 'r7',
      name: 'Marco Diaz',
      ref: 'ONB-2030',
      tintIdx: 1,
      status: 'completed',
      stageLabel: 'Completed',
      stageDone: 4,
      stageTotal: 4,
      started: 'May 18',
      duration: '12d',
      sla: 'Met',
    },
    {
      id: 'r8',
      name: 'Sara Kim',
      ref: 'ONB-2026',
      tintIdx: 4,
      status: 'completed',
      stageLabel: 'Completed',
      stageDone: 4,
      stageTotal: 4,
      started: 'May 9',
      duration: '18d',
      sla: 'Met',
    },
  ];
  return {
    summary:
      'From a signed offer to a productive first 90 days — provisioning, compliance training, manager check-ins and 30/60/90 goals, with gates between every stage.',
    updated: '2 days ago',
    stages: 4,
    steps: 14,
    execKpis: computeExecKpis(runs, '11d'),
    attention: [
      {
        id: 'a1',
        name: 'Alex Rivera',
        ref: 'ONB-2029',
        tintIdx: 0,
        status: 'failed',
        msg: 'Identity provider timed out while provisioning the Okta account.',
        started: 'Started Jul 6 · 2d elapsed',
        warn: true,
      },
      {
        id: 'a2',
        name: 'Nina Park',
        ref: 'ONB-2019',
        tintIdx: 3,
        status: 'exception',
        msg: 'Grant system access failed — the n8n access-provisioning workflow exited with an error.',
        started: 'Started Jun 30 · 8d elapsed',
        warn: true,
      },
      {
        id: 'a3',
        name: 'Jordan Lee',
        ref: 'ONB-2061',
        tintIdx: 2,
        status: 'risk',
        msg: 'Running over SLA in First Week.',
        started: 'Started Jun 28 · 9d elapsed',
        warn: true,
      },
      {
        id: 'a4',
        name: 'Tom Becker',
        ref: 'ONB-2044',
        tintIdx: 4,
        status: 'awaiting',
        msg: 'Waiting on a human in Pre-boarding.',
        started: 'Started Jul 9 · 4h elapsed',
        warn: false,
      },
    ],
    runMeta: runSummary(runs),
    runs,
    anaStats: [
      { label: 'Process runs', value: '160', sub: '+18% vs prior 90d', subColor: 'var(--green)' },
      { label: 'Success rate', value: '92%', sub: 'completed clean', valueColor: 'var(--green)' },
      { label: 'Median completion', value: '11d', sub: 'p50 cycle' },
      { label: 'Escalations', value: '7', sub: '2.1% of runs' },
      { label: 'SLA compliance', value: '88%', sub: 'within target', valueColor: 'var(--green)' },
      { label: 'Approval rejection', value: '6%', sub: 'of approvals' },
    ],
    slowest: [
      { name: 'Background check', value: '3.2d', pct: 100 },
      { name: 'Provision laptop & accounts', value: '2.1d', pct: 66 },
      { name: 'Grant system access', value: '1.6d', pct: 50 },
      { name: 'Security & compliance training', value: '1.1d', pct: 34 },
      { name: 'Confirm signed offer', value: '0.7d', pct: 22 },
    ],
    failed: [
      { name: 'Provision laptop & accounts', value: '8.8%', pct: 100, count: '14 failed runs' },
      { name: 'Background check', value: '5.6%', pct: 64, count: '9 failed runs' },
      { name: 'Grant system access', value: '3.1%', pct: 36, count: '5 failed runs' },
      { name: 'Schedule Manager 1:1', value: '1.9%', pct: 21, count: '3 failed runs' },
    ],
    metrics: [
      {
        id: 'onboarding_completion',
        name: 'Onboarding completion rate',
        conn: 'Workday',
        value: '86%',
        unit: '%',
        dir: 'up',
        tone: 'good',
        spark: [79, 80, 79, 81, 82, 81, 83, 84, 83, 85, 84, 86],
        threshold: { op: 'below', limit: 80 },
      },
      {
        id: 'time_to_productivity',
        name: 'Time to productivity',
        conn: 'Lattice',
        value: '38 days',
        unit: ' days',
        dir: 'down',
        tone: 'good',
        spark: [47, 46, 45, 44, 43, 42, 41, 40, 40, 39, 38, 38],
      },
      {
        id: 'provisioning_sla_breach',
        name: 'Provisioning SLA breach rate',
        conn: 'Okta',
        value: '12%',
        unit: '%',
        dir: 'up',
        tone: 'bad',
        spark: [7, 8, 8, 9, 10, 9, 11, 11, 12, 11, 12, 12],
        threshold: { op: 'above', limit: 10 },
      },
      {
        id: 'newhire_enps',
        name: 'New-hire eNPS',
        conn: 'Lattice',
        value: '48',
        unit: '',
        dir: 'up',
        tone: 'good',
        spark: [40, 41, 42, 43, 44, 45, 46, 46, 47, 47, 48, 48],
      },
      {
        id: 'access_grant_lead',
        name: 'Access-grant lead time',
        conn: 'Okta',
        value: '28h',
        unit: 'h',
        dir: 'up',
        tone: 'warn',
        spark: [22, 23, 23, 24, 25, 25, 26, 27, 27, 28, 28, 28],
        threshold: { op: 'above', limit: 24 },
      },
      {
        id: 'retention_90d',
        name: '90-day new-hire retention',
        conn: 'Workday',
        value: '93%',
        unit: '%',
        dir: 'flat',
        tone: 'good',
        spark: [92, 93, 93, 92, 93, 93, 94, 93, 93, 93, 93, 93],
      },
    ],
    releases: [
      {
        ver: 'v4',
        color: 'var(--brand)',
        note: 'Parallelized Day-1 provisioning & auto-scheduled Manager 1:1s',
      },
      { ver: 'v3', color: 'var(--blue)', note: 'Added background-check gate before Day 1' },
      { ver: 'v2', color: 'var(--teal)', note: 'Tightened laptop SLA to 3 days' },
    ],
    grants: [
      {
        id: 'g1',
        name: 'Dana Cole',
        sub: 'Head of People Ops',
        kind: 'person',
        init: 'DC',
        level: 'Owner',
      },
      {
        id: 'g2',
        name: agent,
        sub: 'Drives every run of this process',
        kind: 'agent',
        init: agent.slice(0, 1),
        level: 'Can manage',
      },
      { id: 'g3', name: 'HR Ops', sub: '9 people', kind: 'team', init: 'HR', level: 'Can manage' },
      {
        id: 'g4',
        name: 'Hiring Managers',
        sub: '24 people',
        kind: 'team',
        init: 'HM',
        level: 'Can run',
      },
      { id: 'g5', name: 'IT Helpdesk', sub: '12 people', kind: 'team', init: 'IT', level: 'Can run' },
      { id: 'g6', name: 'Security', sub: '6 people', kind: 'team', init: 'SE', level: 'Can run' },
      { id: 'g7', name: 'Finance', sub: '7 people', kind: 'team', init: 'FN', level: 'Can view' },
      { id: 'g8', name: 'Everyone at Wand', sub: 'All 320 employees', kind: 'org', level: 'Can view' },
    ],
    accessNote:
      'Anyone at your organization can view this process. Running and managing are limited to the 7 people, teams and agents below.',
    lifecycle: 'Draft',
    approvalVer: 'v5',
    approvalNote:
      'v5 is being authored in the canvas. Submit it for governance review when it’s ready to ship.',
    versions: [
      { ver: 'v5', title: 'New version — being authored', who: 'you', when: 'Draft', state: 'Draft' },
      {
        ver: 'v4',
        title: 'Parallelized Day-1 provisioning & auto-scheduled Manager 1:1',
        who: 'Dana Cole',
        when: '18 days ago',
        state: 'Live',
      },
      {
        ver: 'v3',
        title: 'Added background-check gate before Day 1',
        who: 'Governance team',
        when: '40 days ago',
        state: 'Archived',
      },
      {
        ver: 'v2',
        title: 'Tightened laptop SLA to 3 days',
        who: 'Governance team',
        when: '82 days ago',
        state: 'Archived',
      },
      { ver: 'v1', title: 'Initial deploy', who: 'Marco Reed', when: '5 months ago', state: 'Archived' },
    ],
    audit: [
      {
        cat: 'deploy',
        actor: 'Dana Cole',
        av: 'DC',
        color: 'var(--pink)',
        text: 'deployed version v4 to production',
        time: '2 days ago',
      },
      {
        cat: 'approval',
        actor: 'Governance team',
        av: 'GV',
        color: 'var(--brand)',
        text: 'approved the v4 promotion',
        time: '2 days ago',
      },
      {
        cat: 'approval',
        actor: 'Priya Anand',
        av: 'PA',
        color: 'var(--amber-tx)',
        text: 'requested approval for v4',
        time: '4 days ago',
      },
      {
        cat: 'edit',
        actor: 'Dana Cole',
        av: 'DC',
        color: 'var(--pink)',
        text: 'edited “Provision laptop & accounts” in Day 1',
        time: '4 days ago',
      },
      {
        cat: 'config',
        actor: agent,
        av: agent.slice(0, 2).toUpperCase(),
        color: 'var(--brand)',
        text: 'connected the “Onboarding completion rate” metric',
        time: '1 week ago',
      },
      {
        cat: 'access',
        actor: 'Dana Cole',
        av: 'DC',
        color: 'var(--pink)',
        text: 'granted Hiring Managers “Can run”',
        time: '1 week ago',
      },
      {
        cat: 'access',
        actor: 'Maya Chen',
        av: 'MC',
        color: 'var(--blue)',
        text: 'changed Finance to “Can view”',
        time: '2 weeks ago',
      },
      {
        cat: 'deploy',
        actor: 'System',
        av: 'SY',
        color: 'var(--muted)',
        text: 'archived version v3',
        time: '6 weeks ago',
      },
      {
        cat: 'edit',
        actor: 'Marco Reed',
        av: 'MR',
        color: 'var(--teal)',
        text: 'added the background-check gate',
        time: '6 weeks ago',
      },
    ],
  };
}

/** Neutral base for a pk the org doesn't know (deep link into a missing process). */
const PLACEHOLDER_ROW: ProcessRow = {
  pk: 'unknown',
  name: 'Process',
  cat: '—',
  icon: 'flow',
  accent: 'brand',
  status: 'Draft',
  version: '—',
  active: 0,
  agent: 'Not assigned',
  updated: '—',
  mins: 0,
  desc: '',
};

export function resolveProcessDetail(pk: string, row?: ProcessRow): ProcessDetail {
  // Prefer the org's process row (so non-Wand processes show themselves); the
  // rich body is templated. Icon/accent come from the row via procMeta.
  const base = row ?? { ...PLACEHOLDER_ROW, pk };
  const meta = procMeta(base);
  return {
    pk: base.pk,
    name: base.name,
    cat: base.cat,
    agent: base.agent,
    version: base.version,
    status: base.status,
    icon: meta.icon,
    color: meta.color,
    bg: meta.bg,
    ...template(base.agent),
  };
}
